import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useProfileStore, getFacilityKeyForPortal, getPatientKey } from "@/store/profile";
import patientRequest from "@/api/patient-request";
import { openPortalFile } from "@/utils/portalFile";

/** Shape of /Portal/GetAttachmentList items (camelCased PatientPortalAttachment). */
interface AttachmentItem {
  fileID?: number;
  description?: string;
  fileDate?: string;
  originalFileName?: string;
}

export function DocumentsPage() {
  const currentPatient = useProfileStore((s) => s.currentPatient);
  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!currentPatient) return;
    const facilityKey = getFacilityKeyForPortal();
    const patientKey = getPatientKey();
    if (!facilityKey || !patientKey) return;
    let cancelled = false;
    setLoading(true);
    patientRequest.request
      .getattachmentlist(facilityKey, patientKey)
      .then((res) => {
        if (!cancelled) setAttachments((res?.data as AttachmentItem[]) ?? []);
      })
      .catch(() => {
        if (!cancelled) setAttachments([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentPatient]);

  const [openingId, setOpeningId] = useState<number | null>(null);

  const handleView = async (attachment: AttachmentItem) => {
    if (!currentPatient || attachment.fileID == null) return;
    setOpeningId(attachment.fileID);
    try {
      await openPortalFile({
        facilityId: currentPatient.facilityID,
        fileId: attachment.fileID,
        expectedName: attachment.originalFileName,
      });
    } catch {
      toast.error("We couldn't open that document. Please try again.");
    } finally {
      setOpeningId(null);
    }
  };

  if (!currentPatient) return null;

  return (
    <div className="flex flex-col gap-3">
      <header className="flex shrink-0 items-center">
        <div
          className="mr-3 h-7 w-1 rounded-sm bg-healthsource-navy"
        />
        <h2 className="text-xl font-extrabold text-healthsource-dark">
          Documents
        </h2>
        {attachments.length > 0 && (
          <span
            className="ml-2.5 rounded-xl bg-[var(--hs-tint)] px-2.5 py-0.5 text-xs font-bold text-healthsource-navy"
          >
            {attachments.length}
          </span>
        )}
      </header>

      <div className="sm:overflow-hidden sm:rounded-xl sm:border sm:border-healthsource-lightLavender sm:bg-white sm:shadow-[0_2px_8px_rgba(30,37,43,0.06)]">
        {loading && (
          <div className="py-6 text-center text-sm text-healthsource-gray">
            Loading documents…
          </div>
        )}

        {!loading && attachments.length === 0 && (
          <div className="py-6 text-center text-sm text-healthsource-gray">
            No documents found.
          </div>
        )}

        {!loading && attachments.length > 0 && (
          <ul className="flex flex-col gap-3 sm:gap-3.5 sm:p-5">
            {attachments.map((att, i) => (
              <li
                key={att.fileID ?? i}
                className="flex overflow-hidden rounded-lg border border-healthsource-lightLavender bg-white transition-shadow hover:shadow-md"
                style={{ boxShadow: "0 2px 8px rgba(30,37,43,0.06)" }}
              >
                <div
                  className="w-1 flex-shrink-0 rounded-r-sm bg-healthsource-navy"
                />
                <div className="min-w-0 flex-1 px-4 py-4 sm:pl-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-healthsource-navy/10">
                        <i className="far fa-file-alt text-lg text-healthsource-navy" aria-hidden />
                      </div>
                      <p className="text-[15px] font-extrabold text-healthsource-dark">
                        {att.description || att.originalFileName || "Document"}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleView(att)}
                      disabled={openingId === att.fileID}
                      className="min-h-11 flex-shrink-0 rounded-md px-4 py-2 text-[13px] font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                      style={{ backgroundColor: "var(--hs-red)" }}
                    >
                      {openingId === att.fileID ? "Opening…" : "View"}
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
